import { StateObservable, combineEpics, ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, startWith } from 'rxjs/operators';
import { requestActions } from './apiHelpers';
import AppState from '../reducers/app-state';
import { EnabledNotifications, ReduxAction, UserMetadataFlag } from '@repo/utils/types';
import { apiObservable, searchApiObservable } from '@repo/utils/helpers/api';
import { selectUserMetadata } from '../reducers/user/metadata';
import { Time } from '@internationalized/date';

/* Epic actions */
const userStatsActions = requestActions('USER_STATS', ['fetch']);
const userContactCountActions = requestActions('USER_CONTACT_COUNT', ['fetch']);
const enabledNotificationsActions = requestActions('ENABLED_NOTIFICATIONS', ['fetch', 'update']);
const userMetadataActions = requestActions('USER_METADATA', ['fetch', 'update']);

/* Component actions */
export const fetchUserStats = userStatsActions['fetch'];
export const fetchUserContactCount = userContactCountActions['fetch'];
export const fetchEnabledNotifications = enabledNotificationsActions['fetch'];
export const updateEnabledNotifications = (enabledNotifications: EnabledNotifications) =>
  enabledNotificationsActions['update']({ enabledNotifications });
export const fetchUserMetadata = userMetadataActions['fetch'];
export const setUserMetadataFlag = (flag: UserMetadataFlag, value: boolean) =>
  userMetadataActions['update']({ [flag]: value });
export const updateDefaultReminderTime = (defaultReminderTime: Time) => {
  const { hour, minute } = defaultReminderTime;
  return userMetadataActions['update']({ defaultReminderTime: { hour, minute } });
};

/* Epics */
export const fetchUserStatsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_USER_STATS'),
    mergeMap(() =>
      apiObservable('GET', '/users/self/statistics/').pipe(
        map(userStatsActions['fetchSucceeded']),
        startWith(userStatsActions['fetchRequested']()),
        catchError((err) => of(userStatsActions['fetchFailed'](err))),
      ),
    ),
  );

const fetchUserContactCountEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_USER_CONTACT_COUNT'),
    mergeMap(() =>
      searchApiObservable('GET', 'contacts/count').pipe(
        map(userContactCountActions['fetchSucceeded']),
        startWith(userContactCountActions['fetchRequested']()),
        catchError((err) => of(userContactCountActions['fetchFailed'](err))),
      ),
    ),
  );

const fetchEnabledNotificationsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_ENABLED_NOTIFICATIONS'),
    mergeMap(() =>
      apiObservable('GET', '/users/notifications/settings/').pipe(
        map(enabledNotificationsActions['fetchSucceeded']),
        startWith(enabledNotificationsActions['fetchRequested']()),
        catchError((err) => of(enabledNotificationsActions['fetchFailed'](err))),
      ),
    ),
  );

const updateEnabledNotificationsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('UPDATE_ENABLED_NOTIFICATIONS'),
    mergeMap(({ payload: { enabledNotifications } }) =>
      apiObservable('POST', '/users/notifications/settings/', enabledNotifications).pipe(
        map(enabledNotificationsActions['updateSucceeded']),
        startWith(enabledNotificationsActions['updateRequested'](enabledNotifications)),
        catchError((err) => of(enabledNotificationsActions['updateFailed'](err))),
      ),
    ),
  );

const fetchUserMetadataEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_USER_METADATA'),
    mergeMap(() =>
      apiObservable('GET', '/users/metadata/').pipe(
        map(userMetadataActions['fetchSucceeded']),
        startWith(userMetadataActions['fetchRequested']()),
        catchError((err) => of(userMetadataActions['fetchFailed'](err))),
      ),
    ),
  );

const updateUserMetadataEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('UPDATE_USER_METADATA'),
    mergeMap(({ payload }) => {
      const existingUserMetadata = selectUserMetadata(state$.value);
      const metadata = { ...existingUserMetadata, ...payload };
      return apiObservable('PUT', '/users/metadata/', { metadata }).pipe(
        map(userMetadataActions['updateSucceeded']),
        startWith(userMetadataActions['updateRequested'](metadata)),
        catchError((err) => of(userMetadataActions['updateFailed'](err))),
      );
    }),
  );

export const userEpic = combineEpics(
  fetchUserStatsEpic,
  fetchUserContactCountEpic,
  fetchEnabledNotificationsEpic,
  updateEnabledNotificationsEpic,
  fetchUserMetadataEpic,
  updateUserMetadataEpic,
);
